/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback } from 'react';
import XLSX from 'xlsx-js-style';
import productAtom from 'atom/productAtom';
import { useAtomValue } from 'jotai';

interface ExcelExportProps {
  dataRows: any;
}

const columnTitles = ['구분', '지표', '비율(%)', '인원수(명)'];

const usePersonnelXlsx = ({ dataRows }: ExcelExportProps) => {
  const productNumber = useAtomValue(productAtom);

  const handleClickExcelDownload = useCallback(() => {
    const workbook = XLSX.utils.book_new();

    const data = [
      [{ v: '검사 대상 통계' }],
      [
        ...columnTitles.map((value) => ({
          v: value
        }))
      ],
      ...dataRows.map((result: any) => {
        return result;
      })
    ];

    const worksheet = XLSX.utils.aoa_to_sheet(data);

    worksheet['!cols'] = Array.from({ length: data[1].length }, () => ({
      wpx: 100
    }));
    worksheet['A1'].s = { font: { bold: true, sz: 10, name: '맑은 고딕' } };

    const defaultMerge = [
      { s: { r: 2, c: 0 }, e: { r: 7, c: 0 } },
      { s: { r: 8, c: 0 }, e: { r: 14, c: 0 } },
      { s: { r: 15, c: 0 }, e: { r: 17, c: 0 } }
    ];

    const elementMerge = [
      { s: { r: 2, c: 0 }, e: { r: 8, c: 0 } },
      { s: { r: 9, c: 0 }, e: { r: 11, c: 0 } }
    ];

    const middleMerge = [
      { s: { r: 2, c: 0 }, e: { r: 5, c: 0 } },
      { s: { r: 6, c: 0 }, e: { r: 8, c: 0 } }
    ];

    const healthMerge = [
      { s: { r: 2, c: 0 }, e: { r: 8, c: 0 } },
      { s: { r: 9, c: 0 }, e: { r: 11, c: 0 } }
    ];

    let merges;

    switch (productNumber) {
      case 4:
      case 5:
        merges = defaultMerge;
        break;
      case 8:
        merges = elementMerge;
        break;
      case 9:
        merges = middleMerge;
        break;
      case 15:
      case 1:
        merges = healthMerge;
        break;
      default:
        merges = healthMerge;
        break;
    }

    for (let col = 0; col < data[1].length; col++) {
      const cellAddress = XLSX.utils.encode_cell({ r: 1, c: col });

      worksheet[cellAddress].s = {
        font: { bold: true, sz: 8, name: '맑은 고딕' },
        alignment: { horizontal: 'center', vertical: 'center' },
        fill: { fgColor: { rgb: 'e5e5e5' } },
        border: {
          top: { style: 'thin', color: { auto: 1 } },
          bottom: { style: 'thin', color: { auto: 1 } },
          left: { style: 'thin', color: { auto: 1 } },
          right: { style: 'thin', color: { auto: 1 } }
        }
      };

      for (let i = 2; i < data.length; i++) {
        const cellAddress = XLSX.utils.encode_cell({ r: i, c: col });

        const horizontal = col <= 1 ? 'center' : 'right';
        worksheet[cellAddress].s = {
          font: { sz: 8, bold: col < 1 ? true : false, name: '맑은 고딕' },
          alignment: { horizontal: horizontal, vertical: 'center' },
          fill: col < 1 && { fgColor: { rgb: 'e5e5e5' } },
          border: {
            top: { style: 'thin', color: { auto: 1 } },
            bottom: { style: 'thin', color: { auto: 1 } },
            left: { style: 'thin', color: { auto: 1 } },
            right: { style: 'thin', color: { auto: 1 } }
          }
        };
      }

      for (let i = 0; i < merges.length; i++) {
        const cellAddress = XLSX.utils.encode_cell({
          r: merges[i].e.r,
          c: col
        });
        const horizontal = col <= 1 ? 'center' : 'right';
        worksheet[cellAddress].s = {
          font: { sz: 8, bold: true, name: '맑은 고딕' },
          alignment: { horizontal: horizontal, vertical: 'center' },
          fill: { fgColor: { rgb: 'e5e5e5' } },
          border: {
            top: { style: 'thin', color: { auto: 1 } },
            bottom: { style: 'thin', color: { auto: 1 } },
            left: { style: 'thin', color: { auto: 1 } },
            right: { style: 'thin', color: { auto: 1 } }
          }
        };
      }
    }

    worksheet['!merges'] = merges;

    XLSX.utils.book_append_sheet(workbook, worksheet, '검사대상통계');

    XLSX.writeFile(workbook, '검사대상통계.xlsx');
  }, [columnTitles, dataRows]);

  return { handleClickExcelDownload };
};

export default usePersonnelXlsx;
